import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Avatar,
  Dialog,
  FormControl,
  Grid,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { apiCallVenuesCheckInOut } from "../../../../Core/Apicall";
import CommonPagination from "../../../Common/CommonPagination/CommonPagination";
import EmptyData from "../../../Common/EmptyData/EmptyData";
import CommonShimmer from "../../../Common/CommonShimmer/CommonShimmer";

interface Venue {
  _id: string;
  fullName: string;
  profileImagePath: any;
  isCheckIn: any;
  isCheckOut: any;
  checkInDateTime: string;
  checkInRadius?: number;
  checkOutDateTime: string;
  checkOutRadius?: number;
}

const CheckInTab: React.FC<{ venueId: string }> = ({ venueId }) => {
  const [tableData, setTableData] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMediaForPreview, setSelectedMediaForPreview] = useState<
    any | null
  >(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const totalPages = Math.ceil(totalItems / rowsPerPage);

  // Dropdown filter state
  const [filter, setFilter] = useState("today");
  const [customStartDate, setCustomStartDate] = useState<any>(null);
  const [customEndDate, setCustomEndDate] = useState<any>(null);

  const handleApiCall = async () => {
    setLoading(true);
    try {
      let startDate: string | undefined;
      let endDate: string | undefined;

      const today = dayjs();

      switch (filter) {
        case "today":
          startDate = today.startOf("day").toISOString();
          endDate = today.endOf("day").toISOString();
          break;
        case "yesterday":
          startDate = today.subtract(1, "day").startOf("day").toISOString();
          endDate = today.subtract(1, "day").endOf("day").toISOString();
          break;
        case "week":
          startDate = today.startOf("week").toISOString();
          endDate = today.endOf("week").toISOString();
          break;
        case "month":
          startDate = today.startOf("month").toISOString();
          endDate = today.endOf("month").toISOString();
          break;
        case "custom":
          if (customStartDate && customEndDate) {
            startDate = dayjs(customStartDate).startOf("day").toISOString();
            endDate = dayjs(customEndDate).endOf("day").toISOString();
          }
          break;
        default:
          break;
      }
      const data = {
        venueId,
        offset: page,
        limit: rowsPerPage,
        // type: "checkIn",
        startDate,
        endDate,
      };

      const res = await apiCallVenuesCheckInOut(data);
      setTableData(res.data || []);
      setTotalItems(res?.totalCount || 0);
    } catch (err) {
      console.error("API error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filter === "custom" && !customStartDate && !customEndDate) {
      const today = dayjs();
      setCustomStartDate(today);
      setCustomEndDate(today);
    }
  }, [filter]);

  useEffect(() => {
    handleApiCall();
  }, [page, rowsPerPage, filter, customStartDate, customEndDate]);

  const handlePageChange = (value: number) => setPage(value);

  return (
    <Box sx={{ p: "20px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ width: 200 }}>
          <TextField
            select
            label="Filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              sx: {
                height: 40,
                "& .MuiInputBase-input": {
                  padding: "4px 8px",
                },
              },
            }}
          >
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="yesterday">Yesterday</MenuItem>
            <MenuItem value="week">This Week</MenuItem>
            <MenuItem value="month">This Month</MenuItem>
            <MenuItem value="custom">Custom Date</MenuItem>
          </TextField>
        </Box>

        {filter === "custom" && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              format="DD/MM/YYYY"
              value={customStartDate}
              onChange={(date) => setCustomStartDate(date)}
              slotProps={{
                textField: { size: "small", sx: { width: 150 } },
              }}
            />
            <DatePicker
              label="End Date"
              format="DD/MM/YYYY"
              value={customEndDate}
              onChange={(date) => setCustomEndDate(date)}
              slotProps={{
                textField: { size: "small", sx: { width: 150 } },
              }}
            />
          </LocalizationProvider>
        )}
      </Box>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 3, overflow: "hidden", position: "relative" }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Profile Image</TableCell>
                <TableCell>Full Name</TableCell>
                <TableCell>Check-In Date</TableCell>
                <TableCell>Check-In Radius</TableCell>
                <TableCell>Check-Out Date</TableCell>
                <TableCell>Check-Out Radius</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>

            {!loading && (
              <TableBody>
                {tableData.map((item) => (
                  <TableRow key={item._id}>
                    <TableCell>
                      <Avatar
                        variant="rounded"
                        src={item?.profileImagePath}
                        sx={{
                          width: 65,
                          height: 65,
                          borderRadius: "100px",
                          cursor: "pointer",
                          border: "2px solid #e0e0e0",
                        }}
                        onClick={() =>
                          setSelectedMediaForPreview({
                            type: "Image",
                            url: item?.profileImagePath,
                          })
                        }
                      />
                    </TableCell>

                    <TableCell>{item.fullName}</TableCell>

                    <TableCell>
                      {dayjs(item.checkInDateTime).format(
                        "DD/MM/YYYY, hh:mm A",
                      )}
                    </TableCell>

                    <TableCell>
                      {item?.checkInRadius ? `${item.checkInRadius}m` : "-"}
                    </TableCell>

                    <TableCell>
                      {item?.checkOutDateTime
                        ? dayjs(item.checkOutDateTime).format(
                            "DD/MM/YYYY, hh:mm A",
                          )
                        : "-"}
                    </TableCell>

                    <TableCell>
                      {item?.checkOutRadius ? `${item.checkOutRadius}m` : "-"}
                    </TableCell>

                    {/* ✅ Status Column */}
                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-block",
                          px: 2,
                          py: 0.5,
                          borderRadius: "20px",
                          fontWeight: 600,
                          color: "#fff",
                          backgroundColor: item.isCheckIn
                            ? "#4CAF50" // green
                            : item.isCheckOut
                              ? "#F44336" // red
                              : "#9E9E9E", // gray
                          textAlign: "center",
                          minWidth: 100,
                        }}
                      >
                        {item.isCheckIn
                          ? "Checked In"
                          : item.isCheckOut
                            ? "Checked Out"
                            : "N/A"}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>

          {loading && (
            <Box sx={{ p: 2 }}>
              <CommonShimmer />
            </Box>
          )}

          {!loading && <EmptyData data={tableData} />}
        </TableContainer>

        <CommonPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={totalItems}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(1);
          }}
        />
      </Paper>

      {/* Media Preview Dialog */}
      <Dialog
        open={!!selectedMediaForPreview}
        onClose={() => setSelectedMediaForPreview(null)}
      >
        {selectedMediaForPreview?.type === "Image" && (
          <Avatar
            src={selectedMediaForPreview.url}
            variant="rounded"
            sx={{ width: 300, height: "auto" }}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default CheckInTab;
